/**
 * placementOverrides.ts — PLAT-10921 step 5 (epic decision E7).
 *
 * What happens when the creator moves or trims a graphic's **timeline** edges.
 * One helper for every surface that can do it, so the two entry points can't
 * drift: dragging/trimming **on the timeline** (`TimelineStore.applyPlacements`)
 * and changing the range **in the transcript view** (`LeftSidePanel` /
 * `ClipTranscriptBridge`).
 *
 * ## Recording is unconditional
 *
 * Whenever the creator manually changes a composition's timeline start or end,
 * the corresponding flag is set — regardless of `placementPinned`, regardless of
 * placement. Recording is deliberately separated from enforcing: the flags are a
 * factual record of "the creator touched this edge", kept even while unpinned,
 * which is what makes `placementPinned` safe to flip on later.
 *
 * ## Then each edge is evaluated, independently
 *
 * - **Enforce** — the move doesn't stick. The flag is cleared and the following
 *   reconcile snaps the edge back to its reference.
 * - **Accept** — the edge is free. The value and the flag both stay.
 * - **Rebind** — `selection` only. The value stays and `transcriptAnchor` is
 *   **rewritten** to the words the new range covers. Storing only a new time
 *   would let the very next reconcile drag the graphic back, because the binding
 *   still points at the old words and the words are the source of truth (E4).
 *
 * TIMELINE ONLY. A canvas move or resize never reaches this file.
 */

import { evaluatePlacementEdge } from '@src/editor/lib/placementEvaluation';
import {
  readPlacementMeta,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { resolveTimeRangeAnchor } from '@src/utility/placementAnchor';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { VideoLike } from '@src/utility/extraElementData';
import type { AnchorTranscriptElement } from '@src/utility/placementAnchor';

const logger = LogManager.getLogger('utility:placementOverrides');

/** Sub-frame jitter is not a creator decision. Matches the reconciler's epsilon. */
const EPSILON = 0.001;

export interface TimelineEdgeChange {
  /** Where the element's timeline range was before the creator's edit. */
  previous: { time: number; duration: number };
  /** Where it is after. */
  next: { time: number; duration: number };
}

export interface RecordPlacementEdgeOverridesParams extends TimelineEdgeChange {
  video: VideoLike;
  elementId: string;
  /**
   * The live transcript, needed ONLY to rebind a `selection` graphic's anchor.
   *
   * A thunk, not an array, because that is the whole point: building the full
   * transcript is O(all words) and every timeline drag would pay it, while almost
   * no drag involves a `selection` graphic. It is called at most once, inside the
   * rebind branch.
   *
   * Omit it on a surface that already rewrote `transcriptAnchor` itself, or that
   * has no transcript. A `selection` rebind then keeps its existing anchor and
   * the next reconcile pulls the graphic back to the old words — a visible no-op
   * rather than silent drift, so it is the safe degradation.
   */
  transcript?: () => readonly AnchorTranscriptElement[];
}

/**
 * Record which timeline edges the creator changed, then evaluate each one.
 *
 * Returns whether any meta was written, so the caller knows to hand the updated
 * `extraElementData` back to the editor. Touches neither the editor nor the
 * renderer itself, so it stays usable from a store, a component and a test alike.
 *
 * **Call this BEFORE the command that moves the element.** The command runs a
 * placement reconcile on its way out; if the flags and the rebound
 * `transcriptAnchor` are not in place by then, that reconcile derives from the
 * OLD binding and snaps the graphic straight back.
 */
export function recordPlacementEdgeOverrides({
  video,
  elementId,
  previous,
  next,
  transcript,
}: RecordPlacementEdgeOverridesParams): boolean {
  const root = video?.extraElementData;
  const meta =
    root && typeof root === 'object'
      ? (root as Record<string, unknown>)[elementId]
      : undefined;
  const placementMeta = readPlacementMeta(meta);
  if (!placementMeta) return false;

  const startMoved = Math.abs(next.time - previous.time) > EPSILON;
  const endMoved =
    Math.abs(next.time + next.duration - (previous.time + previous.duration)) >
    EPSILON;
  if (!startMoved && !endMoved) return false;

  const edge = (which: 'start' | 'end') =>
    evaluatePlacementEdge({
      placement: placementMeta.placement,
      edge: which,
      pinned: placementMeta.pinned,
    });
  const startOutcome = edge('start');
  const endOutcome = edge('end');

  const patch: Record<string, unknown> = {};

  // Unconditional recording, then Enforce clears what it just recorded — the
  // flag marks an edge that HOLDS a creator value, and an enforced edge doesn't.
  if (startMoved) {
    patch.placementStartTimeOverride = startOutcome !== 'enforce';
  }
  if (endMoved) {
    patch.placementEndTimeOverride = endOutcome !== 'enforce';
  }

  // Rebind — `selection` only, where both edges rebind, so one anchor rewrite
  // covers whichever moved.
  if (startOutcome === 'rebind' && transcript) {
    const anchor = resolveTimeRangeAnchor(transcript(), {
      time: next.time,
      endTime: next.time + next.duration,
    });
    if (anchor) {
      patch.transcriptAnchor = anchor;
    } else {
      // The new range covers no surviving word. Keep the existing anchor rather
      // than erasing the graphic's only binding.
      logger.warn('Selection rebind found no words — keeping the old anchor', {
        elementId,
        ...next,
      });
    }
  }

  upsertElementMetaObject(video, elementId, patch);

  logger.debug('Recorded timeline placement overrides', {
    elementId,
    placement: placementMeta.placement,
    pinned: placementMeta.pinned,
    startOutcome,
    endOutcome,
    startMoved,
    endMoved,
  });

  return true;
}

/**
 * The element's timeline range as it stands right now. Call this BEFORE the
 * command that changes it, to get the `previous` half of the edit.
 */
export function readElementTimelineRange(
  editor: Pick<IVideoEditor, 'getElementById'>,
  elementId: string,
): { time: number; duration: number } | null {
  const element = editor.getElementById(elementId);
  if (!element) return null;
  return { time: element.time, duration: element.durationSeconds };
}

export interface ApplyPlacementEdgeOverridesParams extends TimelineEdgeChange {
  editor: Pick<IVideoEditor, 'getElementById' | 'passExtraElementData'>;
  video: VideoLike;
  elementId: string;
  /**
   * The live transcript, as a thunk, for a `selection` Rebind. Omit on a surface
   * that has ALREADY rewritten `transcriptAnchor` itself from the words it
   * resolved the range from — recomputing it here would just re-derive the same
   * anchor at the cost of building the whole transcript.
   */
  transcript?: () => readonly AnchorTranscriptElement[];
}

/**
 * Record the edge overrides for one creator edit and hand the updated meta back
 * to the editor, so the reconcile that follows sees them. The thin wrapper the
 * UI surfaces call, so they don't each re-implement the pass-back.
 *
 * **Call this BEFORE the command that moves the element** — see
 * `recordPlacementEdgeOverrides`.
 */
export function applyPlacementEdgeOverrides({
  editor,
  video,
  elementId,
  previous,
  next,
  transcript,
}: ApplyPlacementEdgeOverridesParams): void {
  const changed = recordPlacementEdgeOverrides({
    video,
    elementId,
    previous,
    next,
    transcript,
  });
  if (!changed || !video) return;
  // The editor keeps its own reference to the bag, and the store→editor MobX
  // reaction can lag when the mutation happens inside an action — so the
  // reconcile in the command that follows would otherwise read stale meta.
  editor.passExtraElementData(
    video.extraElementData as Parameters<
      IVideoEditor['passExtraElementData']
    >[0],
  );
}
